import {
  Box,
  Chip,
  createStyles,
  FormControl,
  Grid,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
  withStyles,
} from "@material-ui/core";
import {Pagination} from "@material-ui/lab";
import authApi from "api/authApi";
import {AcademicRecordProps, ResponseAcademicRecord} from "models/auth";
import moment from "moment";
import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {dateTimeToFormatYYYY} from "utils/dateTimeFormat";
import useStyles from "./style";
import {ListParams} from "models/common";
import EmptyComponent from "components/Empty";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import AssignmentTurnedInIcon from "@material-ui/icons/AssignmentTurnedIn";
import EmojiEventsIcon from "@material-ui/icons/EmojiEvents";
import MenuBookIcon from "@material-ui/icons/MenuBook";

interface IListAcademicRecord {
  content: AcademicRecordProps[];
  [key: string]: any;
}
interface IAcademicRecordProps {
  id?: string | undefined;
}
export default function AcademicRecord({id}: IAcademicRecordProps) {
  const StyledTableCell = withStyles((theme: Theme) =>
    createStyles({
      head: {
        padding: "14px 16px",
        background: "linear-gradient(135deg, #0F172A 0%, #1E293B 100%)",
        color: "#FFFFFF",
        fontSize: 13,
        fontWeight: 800,
        letterSpacing: "0.03em",
        borderRight: "1px solid rgba(255, 255, 255, 0.1)",
        borderBottom: "none",
        textTransform: "uppercase",
        [theme.breakpoints?.down("sm")]: {
          fontSize: 10,
          padding: "10px 6px",
        },
      },
      body: {
        padding: "14px 16px",
        fontSize: 13,
        fontWeight: 500,
        color: "#1E293B",
        borderRight: "1px solid #F1F5F9",
        borderBottom: "1px solid #F1F5F9",
        [theme.breakpoints?.down("sm")]: {
          fontSize: 10,
          padding: "10px 6px",
        },
      },
      footer: {
        backgroundColor: "#EEF2FF",
        color: "#0F172A",
        fontSize: 14,
        fontWeight: 800,
        padding: "14px 16px",
        borderRight: "1px solid #C7D2FE",
        borderTop: "2px solid #6366F1",
        [theme.breakpoints?.down("sm")]: {
          fontSize: 11,
          padding: "10px 6px",
        },
      },
    })
  )(TableCell);

  const {t} = useTranslation();
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints?.down("sm"));
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [filter, setFilter] = useState<ListParams>({
    page: 0,
    size: 10,
    userId: Number(id) || "",
  });

  const handleChangeYear = (event: React.ChangeEvent<{value: unknown}>) => {
    setYear(Number(event.target.value));
  };

  const [listAcademicRecord, setListAcademicRecord] = useState<IListAcademicRecord>({
    content: [],
  });

  const [averageYear, setAverageYear] = useState(0);
  useEffect(() => {
    (async () => {
      try {
        const res: ResponseAcademicRecord<AcademicRecordProps> = await authApi.getAcademicRecord({
          ...filter,
          year,
        });
        setAverageYear(res.averageMarkInYear);
        setListAcademicRecord(res.list);
      } catch (error) {
        console.log(error);
      }
    })();
  }, [year, filter, id]);

  const handleChangePage = (page: number) => {
    setFilter({
      ...filter,
      page: page - 1,
    });
  };

  const renderRatingChip = (percent: number) => {
    if (percent < 50) {
      return <Chip label={t("point.yeu")} size="small" style={{ backgroundColor: "#FEF2F2", color: "#DC2626", fontWeight: 800, border: "1px solid #FECACA" }} />;
    } else if (percent < 65) {
      return <Chip label={t("point.trungbinh")} size="small" style={{ backgroundColor: "#F1F5F9", color: "#475569", fontWeight: 800, border: "1px solid #CBD5E1" }} />;
    } else if (percent < 80) {
      return <Chip label={t("point.kha")} size="small" style={{ backgroundColor: "#EFF6FF", color: "#2563EB", fontWeight: 800, border: "1px solid #BFDBFE" }} />;
    } else if (percent < 100) {
      return <Chip label={t("point.gioi")} size="small" style={{ backgroundColor: "#F3E8FF", color: "#7C3AED", fontWeight: 800, border: "1px solid #DDD6FE" }} />;
    } else {
      return <Chip label={t("point.thukhoa")} size="small" style={{ backgroundColor: "#FEF3C7", color: "#D97706", fontWeight: 800, border: "1px solid #FDE68A" }} />;
    }
  };

  const avgPercent = (averageYear * 100);

  return (
    <Box className={classes.container}>
      {/* Header Banner */}
      <Box className={classes.headerBanner}>
        <Box className={classes.headerTitleGroup}>
          <Box className={classes.headerIconPill}>
            <AssignmentTurnedInIcon style={{ fontSize: 26 }} />
          </Box>
          <Box>
            <Typography className={classes.title}>
              {t("academic.hocbacanhan")}
            </Typography>
            <Typography className={classes.subtitle}>
              Tổng hợp bảng điểm thi và kết quả đánh giá năng lực theo năm học
            </Typography>
          </Box>
        </Box>

        <FormControl variant="outlined" className={classes.formControl}>
          <Select
            native
            value={year}
            onChange={handleChangeYear}
            defaultValue={new Date().getFullYear()}
            IconComponent={CalendarTodayIcon}
          >
            {Array.from(new Array(10)).map((id, index) => (
              <option value={index + 2020} key={id}>
                Năm {index + 2020}
              </option>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Summary Score Cards */}
      {listAcademicRecord?.content?.length > 0 && (
        <Grid container spacing={isMobile ? 2 : 3} alignItems="stretch" className={classes.summaryGrid}>
          <Grid item xs={12} md={6}>
            <Box
              className={classes.summaryCard}
              style={{
                background: "linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%)",
                borderColor: "#C7D2FE",
                height: "100%",
                minHeight: 80,
              }}
            >
              <Box>
                <Typography
                  style={{
                    fontSize: "0.775rem",
                    color: "#4338CA",
                    fontWeight: 800,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    marginBottom: 6,
                  }}
                >
                  {t("academic.diemtrungbinhnam")}
                </Typography>
                <Box style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                  <Typography style={{ fontSize: "2.0rem", fontWeight: 900, color: "#4F46E5", lineHeight: 1 }}>
                    {avgPercent.toFixed(1)}
                  </Typography>
                  <Typography style={{ fontSize: "0.95rem", fontWeight: 700, color: "#6366F1" }}>
                    / 100 điểm
                  </Typography>
                </Box>
              </Box>
              <Box style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8 }}>
                <EmojiEventsIcon style={{ color: "#4F46E5", fontSize: 32 }} />
                {renderRatingChip(avgPercent)}
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box
              className={classes.summaryCard}
              style={{
                background: "linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 100%)",
                borderColor: "#BBF7D0",
                height: "100%",
                minHeight: 80,
              }}
            >
              <Box>
                <Typography
                  style={{
                    fontSize: "0.775rem",
                    color: "#15803D",
                    fontWeight: 800,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    marginBottom: 6,
                  }}
                >
                  TỔNG SỐ BÀI THI NĂM {year}
                </Typography>
                <Box style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                  <Typography style={{ fontSize: "2.0rem", fontWeight: 900, color: "#16A34A", lineHeight: 1 }}>
                    {listAcademicRecord?.totalElements || 0}
                  </Typography>
                  <Typography style={{ fontSize: "0.95rem", fontWeight: 700, color: "#22C55E" }}>
                    bài đã thi
                  </Typography>
                </Box>
              </Box>
              <Box style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8 }}>
                <MenuBookIcon style={{ color: "#16A34A", fontSize: 32 }} />
                <Chip
                  label="Đã hoàn thành"
                  size="small"
                  style={{
                    backgroundColor: "#DCFCE7",
                    color: "#15803D",
                    fontWeight: 800,
                    border: "1px solid #86EFAC",
                  }}
                />
              </Box>
            </Box>
          </Grid>
        </Grid>
      )}

      {/* Table Section */}
      {listAcademicRecord?.content?.length > 0 ? (
        <Box>
          {isMobile ? (
            <Box style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {listAcademicRecord.content.map((item: AcademicRecordProps) => {
                const percent = item.maxPoint > 0 ? (item.point / item.maxPoint) * 100 : 0;
                const isSkipped = moment
                  .duration(
                    moment(new Date(item.endDate).getTime()).diff(
                      moment(new Date().getTime())
                    )
                  )
                  .milliseconds() < 0 && !item.isSubmitted;

                return (
                  <Box key={item.id} className={classes.mobileCard}>
                    {/* Top Row: Date & Status */}
                    <Box style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                      <Typography style={{ fontSize: "0.85rem", fontWeight: 700, color: "#64748B" }}>
                        {dateTimeToFormatYYYY(item.startDate)}
                      </Typography>
                      <Box style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        {isSkipped ? (
                          <Chip label={t("academic.bothi")} size="small" style={{ backgroundColor: "#F1F5F9", color: "#64748B", fontWeight: 700 }} />
                        ) : (
                          item.point === null || item.point === undefined
                            ? ""
                            : item.point === 0
                            ? <Chip label={t("academic.rot")} size="small" style={{ backgroundColor: "#FEF2F2", color: "#DC2626", fontWeight: 800, border: "1px solid #FECACA" }} />
                            : <Chip label={t("academic.dau")} size="small" style={{ backgroundColor: "#ECFDF5", color: "#059669", fontWeight: 800, border: "1px solid #A7F3D0" }} />
                        )}
                      </Box>
                    </Box>

                    {/* Middle Row: Exam Title */}
                    <Typography style={{ fontWeight: 800, color: "#1E293B", fontSize: "1.0rem", marginBottom: 12 }}>
                      {item.title}
                    </Typography>

                    {/* Bottom Row: Score & Rating */}
                    <Box style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 10, borderTop: "1px solid #F1F5F9" }}>
                      <Box style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <Typography style={{ fontSize: "0.85rem", fontWeight: 700, color: "#64748B" }}>
                          {t("academic.diemthi")}:
                        </Typography>
                        <Typography style={{ fontSize: "1.1rem", fontWeight: 900, color: "#2563EB" }}>
                          {item.point === null || item.point === undefined ? "-" : Math.floor(item.point)}
                        </Typography>
                      </Box>
                      {renderRatingChip(percent)}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ) : (
            <TableContainer component={Paper} className={classes.container_table}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <StyledTableCell align="center">{t("academic.ngaythi")}</StyledTableCell>
                    <StyledTableCell align="left">{t("academic.tenbaithi")}</StyledTableCell>
                    <StyledTableCell align="center">{t("academic.trangthai")}</StyledTableCell>
                    <StyledTableCell align="center">{t("academic.diemthi")}</StyledTableCell>
                    <StyledTableCell align="center">{t("academic.xeploai")}</StyledTableCell>
                    <StyledTableCell align="center">{t("academic.ghichu")}</StyledTableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {listAcademicRecord?.content?.map((item: AcademicRecordProps) => {
                    const percent = item.maxPoint > 0 ? (item.point / item.maxPoint) * 100 : 0;

                    return (
                      <TableRow key={item.id}>
                        <StyledTableCell align="center" width="130px">
                          {dateTimeToFormatYYYY(item.startDate)}
                        </StyledTableCell>

                        <StyledTableCell align="left" style={{ fontWeight: 700 }}>
                          {item.title}
                        </StyledTableCell>

                        <StyledTableCell align="center" width="130px">
                          {item.point === null || item.point === undefined
                            ? ""
                            : item.point === 0
                            ? <Chip label={t("academic.rot")} size="small" style={{ backgroundColor: "#FEF2F2", color: "#DC2626", fontWeight: 800, border: "1px solid #FECACA" }} />
                            : <Chip label={t("academic.dau")} size="small" style={{ backgroundColor: "#ECFDF5", color: "#059669", fontWeight: 800, border: "1px solid #A7F3D0" }} />}
                        </StyledTableCell>

                        <StyledTableCell align="center" width="130px">
                          <Typography style={{ fontSize: "1.1rem", fontWeight: 900, color: "#2563EB" }}>
                            {Math.floor(item.point)}
                          </Typography>
                        </StyledTableCell>

                        <StyledTableCell align="center" width="140px">
                          {renderRatingChip(percent)}
                        </StyledTableCell>

                        <StyledTableCell align="center" width="130px">
                          {moment
                            .duration(
                              moment(new Date(item.endDate).getTime()).diff(
                                moment(new Date().getTime())
                              )
                            )
                            .milliseconds() < 0 && !item.isSubmitted
                            ? <Chip label={t("academic.bothi")} size="small" style={{ backgroundColor: "#F1F5F9", color: "#64748B", fontWeight: 700 }} />
                            : ""}
                        </StyledTableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>

                <TableFooter>
                  <TableRow>
                    <StyledTableCell
                      colSpan={3}
                      style={{
                        color: "#4F46E5",
                        textAlign: "center",
                        fontSize: 15,
                        fontWeight: 800,
                      }}
                      align="center"
                    >
                      {t("academic.diemtrungbinhnam")}
                    </StyledTableCell>

                    <StyledTableCell align="center" style={{ color: "#4F46E5", fontSize: "1.2rem", fontWeight: 900 }}>
                      {avgPercent.toFixed(1)}
                    </StyledTableCell>

                    <StyledTableCell align="center">
                      {renderRatingChip(avgPercent)}
                    </StyledTableCell>

                    <StyledTableCell align="center" />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          )}

          {listAcademicRecord?.totalPages > 0 && (
            <Box mt={3} display="flex" justifyContent="center" mb={3}>
              <Pagination
                count={Math.ceil(listAcademicRecord?.totalElements / listAcademicRecord?.size)}
                page={listAcademicRecord?.number + 1}
                variant="outlined"
                color="primary"
                shape="rounded"
                onChange={(e, page) => handleChangePage(page)}
              />
            </Box>
          )}
        </Box>
      ) : (
        <Box py={4}>
          <EmptyComponent />
        </Box>
      )}
    </Box>
  );
}
